import { useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  MenuItem,
  Stack,
  Switch,
  TextField,
  Typography
} from '@mui/material';

import PrimaryButton from '../ui/buttons/primaryButton';
import EventChangePreview from './eventChangePreview';
import {
  applyVenueSwap,
  getEventVenueUsage,
  previewVenueSwap
} from '../../features/event/eventSlice';
import { setAlertInfo } from '../../features/alert/alertSlice';
import { getGameByEventId } from '../../features/game/gameSlice';
import { getBracketsByEventId } from '../../features/bracket/bracketSlice';
import venueService from '../../features/venue/venueService';

// The green selector's "Other" entry. Green names are short labels ("A", "F"),
// so this sentinel cannot collide with a real one.
const OTHER_GREEN = '__other__';

const usageSummary = (item) => {
  if (!item) return '';
  const parts = [];
  if (item.eventVenue) parts.push('event venue');
  if (item.locations) parts.push(`${item.locations} location(s)`);
  if (item.greens) parts.push(`${item.greens} green(s)`);
  if (item.rinkPlanEntries) parts.push(`${item.rinkPlanEntries} rink plan(s)`);
  if (item.games) parts.push(`${item.games} game(s)`);
  if (item.bracketMatches) parts.push(`${item.bracketMatches} bracket match(es)`);
  return parts.join(' · ');
};

/**
 * Swaps one source venue for another throughout an event. The source list is
 * the event's own reference inventory, so venues that only exist as a name in
 * older games can still be swapped. Two steps, like the player replacement:
 * choose, review the server preview, then apply with its hash.
 */
const VenueSwapDialog = ({ open, onClose, eventId }) => {
  const dispatch = useDispatch();

  const [step, setStep] = useState('setup');
  const [usage, setUsage] = useState(null);
  const [venues, setVenues] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState('');
  const [source, setSource] = useState(null);
  const [destination, setDestination] = useState(null);
  const [green, setGreen] = useState('');
  const [customGreen, setCustomGreen] = useState('');
  const [sectionGames, setSectionGames] = useState(true);
  const [bracketGames, setBracketGames] = useState(true);
  const [includeCompleted, setIncludeCompleted] = useState(false);
  const [preview, setPreview] = useState(null);
  const [lastPayload, setLastPayload] = useState(null);
  const [appliedResult, setAppliedResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const reset = () => {
    setStep('setup');
    setUsage(null);
    setVenues([]);
    setLoading(false);
    setLoadError('');
    setSource(null);
    setDestination(null);
    setGreen('');
    setCustomGreen('');
    setSectionGames(true);
    setBracketGames(true);
    setIncludeCompleted(false);
    setPreview(null);
    setLastPayload(null);
    setAppliedResult(null);
    setBusy(false);
    setErrorMessage('');
  };

  useEffect(() => {
    if (!open) return undefined;

    reset();
    let active = true;
    setLoading(true);

    Promise.all([
      dispatch(getEventVenueUsage(eventId)).unwrap(),
      venueService.getAll()
    ])
      .then(([usageResult, venueList]) => {
        if (!active) return;
        setUsage(usageResult);
        setVenues(Array.isArray(venueList) ? venueList : []);
      })
      .catch((error) => {
        if (!active) return;
        setLoadError(error?.message || 'The event venues could not be loaded.');
      })
      .finally(() => {
        if (active) setLoading(false);
      });

    return () => {
      active = false;
    };
  }, [open, eventId, dispatch]);

  const destinationOptions = useMemo(
    () =>
      venues
        .filter((venue) => String(venue.id) !== String(source?.id || ''))
        .sort((left, right) =>
          String(left.name || '').localeCompare(String(right.name || ''))
        ),
    [venues, source?.id]
  );

  const destinationGreens = useMemo(() => {
    const greens = Array.isArray(destination?.greens) ? destination.greens : [];
    return greens
      .map((item) => String(item?.name || '').trim())
      .filter((name, index, names) => Boolean(name) && names.indexOf(name) === index);
  }, [destination]);

  const handleClose = () => {
    if (busy) return;
    reset();
    onClose?.();
  };

  const isOtherGreen = green === OTHER_GREEN;
  const selectedGreen = isOtherGreen ? customGreen.trim() : green;
  const missingOtherGreen = isOtherGreen && !customGreen.trim();

  const buildPayload = () => ({
    fromVenueId: source?.id || undefined,
    fromVenueName: source?.name || undefined,
    toVenueId: destination?.id,
    toVenueName: destination?.name,
    green: selectedGreen || undefined,
    swapSectionGames: sectionGames,
    swapBracketGames: bracketGames,
    includeCompleted
  });

  const canPreview =
    Boolean(source?.id || source?.name) && Boolean(destination?.id) && !missingOtherGreen;

  const runPreview = async (payload) => {
    setBusy(true);
    setErrorMessage('');
    try {
      const result = await dispatch(previewVenueSwap({ eventId, payload })).unwrap();
      if (result.alreadyApplied) {
        reset();
        onClose?.();
        dispatch(setAlertInfo('That venue swap has already been applied to this event.'));
        return;
      }
      setPreview(result);
      setLastPayload(payload);
      setStep('review');
    } catch (error) {
      setErrorMessage(error?.message || 'The preview could not be generated.');
    } finally {
      setBusy(false);
    }
  };

  const handlePreview = () => {
    if (!canPreview) return;
    runPreview(buildPayload());
  };

  const handleApply = async () => {
    if (!preview?.previewHash || !lastPayload) return;
    setBusy(true);
    setErrorMessage('');
    try {
      const result = await dispatch(
        applyVenueSwap({
          eventId,
          payload: { ...lastPayload, previewHash: preview.previewHash }
        })
      ).unwrap();

      if (result.alreadyApplied) {
        reset();
        onClose?.();
        dispatch(setAlertInfo('That venue swap has already been applied to this event.'));
        return;
      }

      // Keep the dialog open on the applied summary so the organiser can see
      // exactly what changed; closing it is the only way to dismiss it.
      setAppliedResult(result);
      setStep('done');
      dispatch(getGameByEventId(eventId));
      dispatch(getBracketsByEventId(eventId));
    } catch (error) {
      if (error?.code === 'EVENT_CHANGED') {
        try {
          const fresh = await dispatch(previewVenueSwap({ eventId, payload: lastPayload })).unwrap();
          setPreview(fresh);
          dispatch(
            setAlertInfo('The event changed since the preview. The preview has been refreshed.')
          );
        } catch (previewError) {
          setErrorMessage(previewError?.message || 'The preview could not be refreshed.');
        }
      } else if (error?.code === 'NO_CHANGES' || error?.alreadyApplied) {
        reset();
        onClose?.();
        dispatch(setAlertInfo(error.message || 'There is nothing left to change.'));
      } else {
        const partial = error?.details?.partialFailure
          ? ' Some records were already written — preview again before retrying.'
          : '';
        setErrorMessage(`${error?.message || 'The venue swap failed.'}${partial}`);
      }
    } finally {
      setBusy(false);
    }
  };

  const isReview = step === 'review';
  const isDone = step === 'done';
  let caption = 'Move every reference in this event to another venue.';
  if (isReview) caption = 'Review the changes before applying.';
  if (isDone) caption = 'Summary of what changed.';

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
      <DialogTitle>
        <Typography variant="h6" color="primary">
          {isDone ? 'Venue swap applied' : 'Swap Venue'}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {caption}
        </Typography>
      </DialogTitle>
      <DialogContent dividers sx={{ p: 2 }}>
        {loadError ? (
          <Alert severity="error" sx={{ mb: 2 }}>
            {loadError}
          </Alert>
        ) : null}
        {errorMessage ? (
          <Alert severity="error" sx={{ mb: 2 }}>
            {errorMessage}
          </Alert>
        ) : null}

        {isDone ? (
          <EventChangePreview
            result={{
              ...preview,
              counts: appliedResult?.counts || preview?.counts,
              warnings: appliedResult?.warnings || [],
              errors: [],
              alreadyApplied: false
            }}
            kind="venue"
            applied
          />
        ) : !isReview ? (
          loading ? (
            <Box display="flex" justifyContent="center" py={4}>
              <CircularProgress />
            </Box>
          ) : (
            <Stack gap={2}>
              <Autocomplete
                options={usage?.venues || []}
                value={source}
                onChange={(event, value) => {
                  setSource(value);
                  setDestination(null);
                  setGreen('');
                  setCustomGreen('');
                }}
                getOptionLabel={(option) => option.name || option.id || ''}
                isOptionEqualToValue={(option, value) =>
                  String(option.id || option.name) === String(value.id || value.name)
                }
                renderOption={(props, option) => (
                  <Box component="li" {...props} key={`${option.id || 'name'}-${option.name}`}>
                    <Box>
                      <Typography variant="body2">{option.name || option.id}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {option.id ? `ID ${option.id}` : 'No venue id'}
                        {' · '}
                        {usageSummary(option) || 'Referenced by this event'}
                        {option.known === false ? ' · Not in the venue list' : ''}
                        {option.nameOnly ? ' · Matched by name' : ''}
                      </Typography>
                    </Box>
                  </Box>
                )}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Source venue"
                    helperText={
                      source
                        ? `${source.id ? `ID ${source.id}` : 'No venue id'} · Only venues this event references are listed.`
                        : 'Only venues this event references are listed.'
                    }
                  />
                )}
              />

              <Autocomplete
                options={destinationOptions}
                value={destination}
                onChange={(event, value) => {
                  setDestination(value);
                  setGreen('');
                  setCustomGreen('');
                }}
                getOptionLabel={(option) => option.name || option.id || ''}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                disabled={!source}
                renderOption={(props, option) => (
                  <Box component="li" {...props} key={option.id}>
                    <Box>
                      <Typography variant="body2">{option.name || option.id}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {option.description || `ID ${option.id}`}
                      </Typography>
                    </Box>
                  </Box>
                )}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Destination venue"
                    helperText={
                      source
                        ? 'The destination must already exist. Rink labels are never renumbered.'
                        : 'Choose the source venue first.'
                    }
                  />
                )}
              />

              <TextField
                select
                fullWidth
                label="Green at destination"
                value={green}
                onChange={(event) => {
                  setGreen(event.target.value);
                  if (event.target.value !== OTHER_GREEN) {
                    setCustomGreen('');
                  }
                }}
                disabled={!destination}
                helperText={
                  !destination
                    ? 'Choose the destination venue first.'
                    : 'Changes the green on moved records that already have one. Choose Other to add a green the venue does not have yet. Rinks are kept.'
                }>
                <MenuItem value="">Keep current green labels</MenuItem>
                {destinationGreens.map((name) => (
                  <MenuItem key={name} value={name}>
                    {name}
                  </MenuItem>
                ))}
                <MenuItem value={OTHER_GREEN}>Other (not listed)</MenuItem>
              </TextField>

              {isOtherGreen ? (
                <TextField
                  fullWidth
                  autoFocus
                  label="New green label"
                  placeholder="e.g. F"
                  value={customGreen}
                  onChange={(event) => setCustomGreen(event.target.value)}
                  error={missingOtherGreen}
                  helperText={
                    missingOtherGreen
                      ? 'Type the green label to assign to the moved records.'
                      : `The destination venue gains this green with the rinks the moved records use. No greens are removed.`
                  }
                />
              ) : null}

              <Box>
                <Typography variant="subtitle2" gutterBottom>
                  Games to change
                </Typography>
                <Stack gap={0.5}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        size="small"
                        checked={sectionGames}
                        onChange={(event) => setSectionGames(event.target.checked)}
                      />
                    }
                    label={
                      <Typography variant="body2">
                        Section games
                        <Typography variant="caption" color="text.secondary" display="block">
                          Round-robin games played in sections.
                        </Typography>
                      </Typography>
                    }
                  />
                  <FormControlLabel
                    control={
                      <Checkbox
                        size="small"
                        checked={bracketGames}
                        onChange={(event) => setBracketGames(event.target.checked)}
                      />
                    }
                    label={
                      <Typography variant="body2">
                        Bracket games
                        <Typography variant="caption" color="text.secondary" display="block">
                          Knockout and playoff games and their bracket matches.
                        </Typography>
                      </Typography>
                    }
                  />
                </Stack>
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
                  The event venue, location lists and section rink plans always change. Games
                  without their own venue follow the event venue.
                </Typography>
              </Box>

              <FormControlLabel
                control={
                  <Switch
                    checked={includeCompleted}
                    onChange={(event) => setIncludeCompleted(event.target.checked)}
                  />
                }
                label={
                  <Typography variant="body2">
                    Include completed records
                    <Typography variant="caption" color="text.secondary" display="block">
                      Also update completed games and completed bracket matches. Finalised
                      section metadata always follows the venue.
                    </Typography>
                  </Typography>
                }
              />

              {usage && usage.unresolved?.length > 0 ? (
                <Alert severity="info">
                  {usage.unresolved.length} venue reference(s) in this event are name-only and not
                  in the venue list. They can still be swapped.
                </Alert>
              ) : null}
            </Stack>
          )
        ) : (
          <EventChangePreview result={preview} kind="venue" />
        )}
      </DialogContent>
      <DialogActions sx={{ p: 2 }}>
        {isDone ? (
          <Button onClick={handleClose}>Close</Button>
        ) : !isReview ? (
          <>
            <Button onClick={handleClose} disabled={busy}>
              Cancel
            </Button>
            <PrimaryButton
              variant="contained"
              onClick={handlePreview}
              disabled={!canPreview || busy || loading}>
              {busy ? <CircularProgress size={20} /> : 'Preview changes'}
            </PrimaryButton>
          </>
        ) : (
          <>
            <Button onClick={() => setStep('setup')} disabled={busy}>
              Back
            </Button>
            <PrimaryButton
              variant="contained"
              onClick={handleApply}
              disabled={busy || !preview?.previewHash || Boolean(preview?.blocked)}>
              {busy ? <CircularProgress size={20} /> : 'Apply venue swap'}
            </PrimaryButton>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default VenueSwapDialog;
